import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle2, PhoneIcon } from 'lucide-react';
import { trackLead, trackContact } from '../utils/pixel';
export function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pulse, setPulse] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('name', formData.name);
      params.append('phone', formData.phone);
      params.append('email', formData.email);
      await fetch('https://services.leadconnectorhq.com/hooks/9bw9Tk4GstO34KNKeVbp/webhook-trigger/e1445ae6-0937-44fc-80d1-08e0bd868380', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString(),
        mode: 'no-cors'
      });
    } catch {
      // Submit silently
    }
    trackLead({
      name: formData.name,
      phone: formData.phone,
      email: formData.email
    });
    setIsSubmitting(false);
    setSubmitted(true);
  };
  // Pulse animation loop every 10 seconds
  useEffect(() => {
    if (!submitted) return;
    const interval = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 800);
    }, 10000);
    // Trigger first pulse after 2 seconds
    const initialTimeout = setTimeout(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 800);
    }, 2000);
    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };
  }, [submitted]);
  const variants = {
    enter: {
      opacity: 0,
      y: 10
    },
    center: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -10
    }
  };
  return <div className="bg-white rounded-2xl p-5 md:p-8 w-full max-w-md mx-auto shadow-[0_8px_40px_rgba(0,0,0,0.25)]" id="lead-form" data-id="element-130">
      <AnimatePresence mode="wait" data-id="element-131">
        {/* STEP 1: Form */}
        {!submitted && <motion.div key="form-step" variants={variants} initial="enter" animate="center" exit="exit" transition={{
        duration: 0.3
      }} data-id="element-132">
            <div className="mb-4 lg:mb-6 text-center" data-id="element-133">
              <h3 className="text-2xl font-light text-brand-dark mb-1 lg:mb-2" data-id="element-134">
                Claim Your $3,000 Off
              </h3>
              <p className="text-sm text-brand-dark/50" data-id="element-135">
                Free Consultation + Limited Time Offer
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col space-y-3 lg:space-y-4" data-id="element-136">
              <div data-id="element-137">
                <label htmlFor="name" className="block text-sm font-medium text-brand-dark/70 mb-1" data-id="element-138">
                  Full Name
                </label>
                <input type="text" id="name" required className="w-full px-4 py-3 lg:py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-brand-dark placeholder-brand-dark/30 focus:ring-2 focus:ring-brand-dark/20 focus:border-brand-dark/30 outline-none transition-all text-base" placeholder="Jane Doe" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} data-id="element-139" />
              </div>
              <div data-id="element-140">
                <label htmlFor="phone" className="block text-sm font-medium text-brand-dark/70 mb-1" data-id="element-141">
                  Phone Number
                </label>
                <input type="tel" id="phone" required className="w-full px-4 py-3 lg:py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-brand-dark placeholder-brand-dark/30 focus:ring-2 focus:ring-brand-dark/20 focus:border-brand-dark/30 outline-none transition-all text-base" placeholder="(305) 555-0123" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} data-id="element-142" />
              </div>
              <div data-id="element-143">
                <label htmlFor="email" className="block text-sm font-medium text-brand-dark/70 mb-1" data-id="element-144">
                  Email Address
                </label>
                <input type="email" id="email" required className="w-full px-4 py-3 lg:py-3.5 rounded-lg bg-gray-50 border border-gray-200 text-brand-dark placeholder-brand-dark/30 focus:ring-2 focus:ring-brand-dark/20 focus:border-brand-dark/30 outline-none transition-all text-base" placeholder="jane@example.com" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} data-id="element-145" />
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-brand-dark hover:bg-brand-darkAlt text-white font-medium py-3.5 lg:py-4 rounded-full transition-colors flex items-center justify-center gap-2 mt-2 text-base" data-id="element-146">
                {isSubmitting ? <>
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-147" />
                    Processing...
                  </> : 'Claim My $3,000 Off'}
              </button>
              <p className="text-xs text-brand-dark/40 text-center pt-1" data-id="element-148">
                No commitment required. We'll call within 5 minutes.
              </p>
            </form>
          </motion.div>}

        {/* STEP 2: Success + Call Now */}
        {submitted && <motion.div key="success" variants={variants} initial="enter" animate="center" transition={{
        duration: 0.3
      }} data-id="element-149">
            <div className="flex flex-col items-center justify-center text-center py-6 px-4" data-id="element-150">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4" data-id="element-151">
                <CheckCircle2 className="w-8 h-8 text-green-500" data-id="element-152" />
              </div>
              <h4 className="text-xl font-bold text-brand-dark mb-2" data-id="element-153">
                Offer Claimed!
              </h4>
              <p className="text-brand-dark/60 mb-6" data-id="element-154">
                Thank you, {formData.name.split(' ')[0] || 'there'}. Our team
                will call you at <strong data-id="element-155">{formData.phone}</strong> shortly to
                book your free consultation.
              </p>

              <div className="w-full border-t border-gray-100 pt-6" data-id="element-156">
                <p className="text-sm font-medium text-brand-dark/70 mb-3" data-id="element-157">
                  Don't want to wait?
                </p>
                <motion.a href="tel:+13055562383" onClick={() => trackContact()} animate={pulse ? {
              scale: [1, 1.04, 1]
            } : {}} transition={{
              duration: 0.6,
              ease: 'easeInOut'
            }} className="w-full flex items-center justify-center gap-2 bg-brand-dark hover:bg-brand-darkAlt text-white font-medium py-3.5 rounded-full transition-colors text-sm" data-id="element-158">
                  <PhoneIcon className="w-4 h-4" data-id="element-159" />
                  Call Us Now — (305) 556-2383
                </motion.a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}